import { Card } from '@/components/ui/card';
import { BarChart3 } from 'lucide-react';
import type { BusinessMetricsDto } from '../models/types';

interface BusinessMetricsCardProps {
  businessMetrics: BusinessMetricsDto[];
}

export const BusinessMetricsCard: React.FC<BusinessMetricsCardProps> = ({ businessMetrics = [] }) => {
  const formatNumber = (num: number): string => {
    return num.toLocaleString('en-US');
  };

  const formatValue = (value: number, metricName: string): string => {
    // Format based on metric type
    if (metricName.includes('rate') || metricName.includes('percentage')) {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (metricName.includes('time') || metricName.includes('latency')) {
      return `${value.toFixed(0)} ms`;
    }
    return formatNumber(value);
  };

  return (
    <Card>
      <div className="flex items-center gap-3 p-4 pb-0">
        <BarChart3 className="h-6 w-6 text-accent-text" />
        <div>
          <p className="text-sm font-semibold text-foreground">Business Metrics</p>
          <span className="text-xs text-muted-foreground">Analytics from external sources</span>
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {businessMetrics.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <span className="text-xs text-muted-foreground">No metrics available</span>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {businessMetrics.map((metric, index) => (
              <div
                key={`${metric.source}-${metric.metricName}-${metric.timestamp}-${index}`}
                className="border-b border-border"
                style={{
                  padding: '8px 0',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span className="text-xs font-semibold">{metric.metricName}</span>
                  <p className="text-sm">{formatValue(metric.value, metric.metricName)}</p>
                </div>
                <span className="block text-xs text-muted-foreground">
                  {metric.source} • {new Date(metric.timestamp).toLocaleString()}
                </span>
                {metric.dimensions && Object.keys(metric.dimensions).length > 0 && (
                  <span className="block text-xs text-muted-foreground" style={{ marginTop: '4px' }}>
                    {Object.entries(metric.dimensions).map(([key, value]) => `${key}: ${value}`).join(', ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
